import { useMemo, useState, useEffect } from 'react';
import StatGraphCard from '../objects/StatGraphCard';
import '../styles/AdminDashboard.css';
import { Chart, ArcElement, Tooltip, Legend } from 'chart.js';
import RemovedJobsList from './RemovedJobsList';
import * as Sentry from '@sentry/react';

Chart.register(ArcElement, Tooltip, Legend);

function hexToRgba(hex, alpha = 1) {
    if (!hex) return `rgba(219, 58, 0, ${alpha})`; // Rezify orange fallback
    const c = hex.replace('#', '');
    const n = parseInt(c, 16);
    const r = (n >> 16) & 255;
    const g = (n >> 8) & 255;
    const b = n & 255;
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
}

function toSortedPairs(dailyObj) {
    return Object.keys(dailyObj || {})
        .sort((a, b) => new Date(a) - new Date(b))
        .map(d => [d, dailyObj[d]]);
}

function labelsFromPairs(pairs) {
    return pairs.map(([d]) => d);
}

const COLOR_POOL = [
    '#FF6B6B',
    '#FFD93D',
    '#6BCB77',
    '#4D96FF',
    '#9D4EDD',
    '#FF914D',
    '#00C49A',
    '#FF66C4',
    '#C77DFF',
    '#6E44FF',
];

function pickColorForKey(key, primaryColor, idx) {
    if (key === 'internships_total') {
        return {
            bg: hexToRgba(primaryColor, 0.45),
            border: primaryColor,
        };
    }

    const base = COLOR_POOL[idx % COLOR_POOL.length];
    return {
        bg: hexToRgba(base, 0.5),
        border: base,
    };
}

function buildStackedFromDaily({
    pairs,
    keys,
    primaryColor,
    addOverlayTotal = true,
    stackId = 'stack1',
    labelMap = {},
}) {
    const labels = labelsFromPairs(pairs);

    const seriesDatasets = keys.map((key, i) => {
        const c = pickColorForKey(key, primaryColor, i);
        const displayName = labelMap[key] || key;

        return {
            label: displayName,
            data: pairs.map(([, row]) => Number(row?.[key] ?? 0)),
            stack: stackId,
            fill: true,
            tension: 0.25,
            backgroundColor: c.bg,
            borderColor: c.border,
            pointBackgroundColor: c.bg,
            pointBorderColor: c.border,
            borderWidth: 2,
        };
    });

    const datasets = [...seriesDatasets];

    if (addOverlayTotal) {
        const total = labels.map((_, idx) =>
            keys.reduce((sum, key) => sum + Number(pairs[idx][1]?.[key] ?? 0), 0)
        );
        datasets.push({
            label: 'Total',
            data: total,
            type: 'line',
            yAxisID: 'y_total',
            fill: false,
            tension: 0.25,
            borderWidth: 2,
            borderColor: '#222',
            pointBackgroundColor: '#222',
            pointBorderColor: '#222',
        });
    }

    return { labels, datasets };
}

function buildLineSeries({
    pairs,
    key,
    primaryColor,
    label,
}) {
    const labelsArr = labelsFromPairs(pairs);
    return {
        labels: labelsArr,
        datasets: [
            {
                label,
                data: pairs.map(([, row]) => Number(row?.[key] ?? 0)),
                fill: true,
                borderColor: primaryColor,
                backgroundColor: hexToRgba(primaryColor, 0.35),
                pointBackgroundColor: hexToRgba(primaryColor, 0.35),
                pointBorderColor: primaryColor,
                borderWidth: 2,
                tension: 0.15,
            }
        ]
    };
}

/**
 * =========================
 * HARD-CODED CONFIG (EDIT ME)
 * =========================
 *
 * - Add/remove keys here to control what shows up.
 * - labels/descriptions are explicitly hard-coded (no string replace magic).
 */
const CLEANING_GRAPH_CONFIG = {
    // Stacked deletions chart (keys ending in _deleted from backend)
    deleted_stacked: {
        key: 'all_deleted',
        label: 'Jobs Deleted (All Types)',
        description: 'Daily deletions stacked by deletion type, with a total overlay.',
        keys: [
            'age_deleted',
            'deduplicate_deleted',
            'scam_deleted',
            'playwright_deleted',
            'specific_site_deleted',
            'request_text_deleted',
            'response_status_link_deleted',
        ],
        labelMap: {
            age_deleted: 'Age > 90 Days',
            deduplicate_deleted: 'Duplicates',
            scam_deleted: 'Scam',
            playwright_deleted: 'Playwright',
            specific_site_deleted: 'Specific Site',
            request_text_deleted: 'Request Text',
            response_status_link_deleted: 'Bad Status / Expired Link',
        },
    },

    // Percent tabs (these are LINE graphs)
    percent_tabs: [
        {
            key: 'playwright_run_perc',
            label: 'Playwright Run %',
            description: '% of total link checks that used Playwright.',
        },
        {
            key: 'perc_of_playwright_del',
            label: 'Playwright Deleted %',
            description: '% of Playwright-ran checks that were deleted.',
        },
        {
            key: 'specific_site_run_perc',
            label: 'Specific Site Run %',
            description: '% of total checks handled by a site-specific checker.',
        },
        {
            key: 'perc_of_specific_site_del',
            label: 'Specific Site Deleted %',
            description: '% of site-specific checks that were deleted.',
        },
        {
            key: 'perc_playwright_timeouts',
            label: 'Playwright Timeout %',
            description: '% of Playwright runs that timed out.',
        },
        {
            key: 'other_errors_perc',
            label: 'Other Errors %',
            description: '% of total runs that hit an error not categorized elsewhere.',
        },
        {
            key: 'request_text_deleted_perc',
            label: 'Request Text Deleted %',
            description: '% of total runs deleted due to request/body text signals.',
        },
        {
            key: 'response_status_link_deleted_perc',
            label: 'Bad Status/Expired Link Deleted %',
            description: '% of total runs deleted due to status code / link response rules.',
        },
    ],
};

export default function JobsDashboard({ admin, primaryColor }) {
    const [stats, setStats] = useState(null);
    const [loading, setLoading] = useState(true);
    const [errorMessage, setErrorMessage] = useState('');
    const [loadedData, setLoadedData] = useState([]);
    const [deletionsObj, setDeletionsObj] = useState({});

    const baseDatasetStyle = useMemo(() => ({
        fill: true,
        borderColor: primaryColor,
        backgroundColor: hexToRgba(primaryColor, 0.6),
        pointBackgroundColor: hexToRgba(primaryColor, 0.6),
        pointBorderColor: primaryColor,
        borderWidth: 2,
        tension: 0.1,
    }), [primaryColor]);

    useEffect(() => {
        let isMounted = true;
        setLoading(true);

        (async () => {
            try {
                const res = await fetch('/api/get_superadmin_jobs_stats');
                const data = await res.json();
                if (!isMounted) return;

                if (!res.ok) {
                    if (data.error_code === 'session_expired') {
                        window.location.href = '/';
                        return;
                    }
                    setErrorMessage(data.error_message || 'Failed to load jobs stats. Please try again.');
                    return;
                }

                if (data.stats) {
                    setStats(data.stats);

                    if (data.stats.jobs_historical) {
                        const sorted = Object.keys(data.stats.jobs_historical)
                            .map(k => [k, data.stats.jobs_historical[k]])
                            .sort((a, b) => new Date(a[0]) - new Date(b[0]));
                        setLoadedData(sorted);
                        setDeletionsObj(data.stats.jobs_historical);
                    }
                }

                if (data.error_message) setErrorMessage(data.error_message);
            } catch (e) {
                if (isMounted) setErrorMessage('Failed to load jobs stats. Please try again.');
                Sentry.captureException(e);
            } finally {
                if (isMounted) setLoading(false);
            }
        })();

        return () => { isMounted = false; };
    }, []);

    const labels = useMemo(() => loadedData.map(([date]) => date), [loadedData]);

    // Graph 1: Number of internships
    const numInternshipsData = useMemo(() => ({
        labels,
        datasets: [{
            label: 'Number of Internships',
            data: loadedData.map(([, v]) => Number(v?.internships_total ?? 0)),
            ...baseDatasetStyle,
        }],
    }), [labels, loadedData, baseDatasetStyle]);

    const dailyPairs = useMemo(() => toSortedPairs(deletionsObj), [deletionsObj]);

    // Graph 1: Credits used (stacked)
    const creditsUsedData = useMemo(() => buildStackedFromDaily({
        pairs: dailyPairs,
        keys: ['cut_internships', 'cut_fulltime'],
        primaryColor,
        addOverlayTotal: true,
        stackId: 'credits',
        labelMap: {
            cut_internships: 'Credits From Internships',
            cut_fulltime: 'Credits From Full-Time Jobs',
        },
    }), [dailyPairs, primaryColor]);

    // Graph 2: Stacked deletions (hard-coded)
    const allJobsDeletedStacked = useMemo(() => {
        const cfg = CLEANING_GRAPH_CONFIG.deleted_stacked;
        return buildStackedFromDaily({
            pairs: dailyPairs,
            keys: cfg.keys,
            primaryColor,
            addOverlayTotal: true,
            stackId: 'all_deleted',
            labelMap: cfg.labelMap,
        });
    }, [dailyPairs, primaryColor]);

    // Graph 2: Percent tabs (hard-coded)
    const percentOptions = useMemo(() => {
        return CLEANING_GRAPH_CONFIG.percent_tabs
            .filter(tab => {
                // Only show if key exists in data somewhere (prevents empty graphs)
                if (!dailyPairs?.length) return false;
                const sample = dailyPairs[dailyPairs.length - 1]?.[1] || {};
                return Object.prototype.hasOwnProperty.call(sample, tab.key);
            })
            .map(tab => ({
                key: tab.key,
                label: tab.label,
                description: tab.description,
                data: buildLineSeries({
                    pairs: dailyPairs,
                    key: tab.key,
                    primaryColor,
                    label: tab.label,
                }),
            }));
    }, [dailyPairs, primaryColor]);

    if (loading) {
        return (
            <div className="spinner-container">
                <div className="spinner-aw" />
            </div>
        );
    }

    return (
        <div className="main-dashboard">
            <h1 style={{ fontFamily: "'Orbitron', sans-serif", marginTop: 0 }}>
                <span style={{ color: 'black' }}>Jobs Overview Dashboard:</span>{' '}
                <span style={{ color: 'var(--primary-color)' }}>
                    {admin.school_fullname}
                </span>
            </h1>

            <div className="grid">
                <StatCard title="Number of Internships" value={stats?.internships_in_table ?? 0} />
                <StatCard title="Title Embeddings" value={stats?.internships_title_embeddings ?? 0} />
                <StatCard title="Description Embeddings" value={stats?.internships_description_embeddings ?? 0} />
                <StatCard
                    title="% of TS Credits Used"
                    value={`${stats?.ts_credits_percentage?.ts_credits_percentage ?? 0}%`}
                    subvalue={`Credits Used: ${stats?.ts_credits_percentage?.ts_credits_used ?? 0}`}
                />
            </div>

            {/* Graph 1: Keep it here, but ONLY internships + credits used */}
            <div className="grid" style={{ padding: '0 0.2rem' }}>
                <StatGraphCard
                    title="Usage Trends"
                    initialKey="internships"
                    defaultRangeKey="1y"
                    options={[
                        {
                            key: 'internships',
                            label: 'Number of Internships',
                            data: numInternshipsData,
                            description: 'Viewing the number of internships in our database.',
                        },
                        {
                            key: 'credits_used',
                            label: 'Credits Used Daily',
                            description: 'Daily credits used split by internships vs fulltime, with a total overlay.',
                            stacked: true,
                            overlayTotal: true,
                            data: creditsUsedData
                        }
                    ]}
                />
            </div>

            <div className="grid">
                <StatCard title="30 Day Credits AVG" value={stats?.thirty_day_credits_avg ?? 0} />
                <StatCard
                    title="Last TheirStack Purchase"
                    value={`${stats?.latest_purchase?.date ?? 'Unknown'} (${stats?.latest_purchase?.days_since_purchase ?? '0'}d)`}
                    subvalue={
                        <>
                            Credits Bought: {stats?.latest_purchase?.credits_purchased ?? 0}
                            <br />
                            Amount Spent: {stats?.latest_purchase?.amount_spent ?? 0}$
                        </>
                    }
                />
                <StatCard
                    title="Estimated Next Credit Purchase"
                    value={`${stats?.estimated_next_credit_purchase?.estimated_next_purchase ?? 'Unknown'} (${stats?.estimated_next_credit_purchase?.days_remaining ?? '0'}d)`}
                />
            </div>

            {/* Graph 2: Cleaning performance (hard-coded labels + descriptions) */}
            <div className="grid" style={{ padding: '0 0.2rem' }}>
                <StatGraphCard
                    title="Cleaning Performance"
                    initialKey={CLEANING_GRAPH_CONFIG.deleted_stacked.key}
                    defaultRangeKey="1y"
                    options={[
                        {
                            key: CLEANING_GRAPH_CONFIG.deleted_stacked.key,
                            label: CLEANING_GRAPH_CONFIG.deleted_stacked.label,
                            description: CLEANING_GRAPH_CONFIG.deleted_stacked.description,
                            stacked: true,
                            overlayTotal: true,
                            data: allJobsDeletedStacked,
                        },
                        ...percentOptions
                    ]}
                />
            </div>

            <div className="grid">
                <div className="stat-card">
                    <h3>Jobs Removed By Users</h3>
                    <RemovedJobsList jobs={stats?.removed_jobs} />
                </div>
            </div>
        </div>
    );
}

function StatCard({ title, value, subvalue }) {
    return (
        <div className="stat-card">
            <h3>{title}</h3>
            <p>{value}</p>
            {subvalue && <p style={{ fontSize: '12px', color: '#595959' }}>{subvalue}</p>}
        </div>
    );
}
